import { useCallback, useEffect, useRef } from 'react';
import { tv } from 'tailwind-variants';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Form,
  Loader,
  Separator,
} from '@/shared/components';
import {
  EXCHANGE_SECTION_TYPES,
  useExchangeAssets,
  useExchangeForm,
} from '../model';
import { useExchangeConversion } from '../model/useExchangeConversion';
import {
  ExchangeInputSection,
  ExchangePercentageSection,
  ExchangeRotateButton,
} from '.';
import { useMediaQuery } from '@/shared/lib';
import { useLocation } from 'react-router-dom';
import { AssetFromAvailableDescription } from './AssetFromAvailableDescription';

const exchangeScreenStyles = tv({
  slots: {
    card: 'rounded-[2.4rem] max-w-[48rem] w-full',
    header: 'p-0',
    content: 'flex flex-col gap-[3.2rem] p-0',
    formStyles: 'flex flex-col gap-[1.6rem]',
    title: 'headline1',
    controlsSection: 'flex flex-col gap-[2.4rem]',
  },
  variants: {
    isMobile: {
      true: {
        card: 'p-[2.4rem]',
      },
      false: {
        card: 'p-[3.2rem]',
      },
    },
  },
  defaultVariants: {
    isMobile: false,
  },
});

const ExchangeScreen = () => {
  const { state } = useLocation();
  const isMobile = useMediaQuery('(max-width: 530px)');
  const { t } = useTranslation();
  const { card, header, content, title, formStyles, controlsSection } =
    exchangeScreenStyles();

  const {
    assetsExchangeFrom,
    assetsExchangeTo,
    selectedAssetToPay,
    setSelectedAssetToPay,
    selectedAssetToReceive,
    setSelectedAssetToReceive,
    resetSelectedAssets,
    isExchangeAssetsLoading,
  } = useExchangeAssets();

  const {
    form,
    assetCodeToPay,
    assetCodeToReceive,
    amountToPay,
    amountToReceive,
    payFieldErrorBeforeClick,
    receiveFieldErrorBeforeClick,
    onPayAmountBlur,
    onReceiveAmountBlur,
    formErrors,
    resetFormErrors,
    onSubmit,
    setExchangeRate,
    setAmountToPay,
    setAmountToReceive,
    setAssetCodeToPay,
    setAssetCodeToReceive,
    resetForm,
  } = useExchangeForm(assetsExchangeFrom, assetsExchangeTo);

  const pendingRotateRef = useRef<{
    assetFrom: string;
    assetTo: string;
    amount: number;
    amountToReceive: number;
  } | null>(null);
  const isRotatingRef = useRef(false);

  const rotateAssets = useCallback(() => {
    if (!assetCodeToPay && !assetCodeToReceive) return;

    const currentAssetFrom = assetCodeToPay || '';
    const currentAssetTo = assetCodeToReceive || '';

    pendingRotateRef.current = {
      assetFrom: currentAssetTo,
      assetTo: currentAssetFrom,
      amount: 0,
      amountToReceive: 0,
    };

    isRotatingRef.current = true;
    resetSelectedAssets();
  }, [assetCodeToPay, assetCodeToReceive, resetSelectedAssets]);

  useEffect(() => {
    if (!pendingRotateRef.current) return;

    if (selectedAssetToPay || selectedAssetToReceive) {
      return;
    }

    const swap = pendingRotateRef.current;
    const { assetFrom, assetTo } = swap;

    if (!assetFrom && !assetTo) {
      pendingRotateRef.current = null;
      isRotatingRef.current = false;
      return;
    }

    resetForm({
      assetFrom: assetFrom || '',
      assetTo: assetTo || '',
      amount: '',
    });
    setAmountToReceive('');

    pendingRotateRef.current = null;
    isRotatingRef.current = false;
  }, [
    selectedAssetToPay,
    selectedAssetToReceive,
    assetsExchangeFrom,
    assetsExchangeTo,
    form,
    resetForm,
    setAmountToReceive,
  ]);

  const {
    exchangeRateDescription,
    recalculateAmountToReceive,
    recalculateAmountToPay,
  } = useExchangeConversion({
    amountToPay,
    amountToReceive,
    setExchangeRate,
    setAmountToPay,
    setAmountToReceive,
    assetCodeToPay,
    assetCodeToReceive,
  });

  useEffect(() => {
    if (isRotatingRef.current) return;

    const newToPay = assetCodeToPay
      ? assetsExchangeFrom.find(asset => asset.code === assetCodeToPay)
      : undefined;

    const newToReceive = assetCodeToReceive
      ? assetsExchangeTo.find(asset => asset.code === assetCodeToReceive)
      : undefined;

    if (newToPay?.code !== selectedAssetToPay?.code) {
      setSelectedAssetToPay(newToPay);
    }

    if (newToReceive?.code !== selectedAssetToReceive?.code) {
      setSelectedAssetToReceive(newToReceive);
    }
  }, [
    assetCodeToPay,
    assetCodeToReceive,
    assetsExchangeFrom,
    assetsExchangeTo,
    selectedAssetToPay?.code,
    selectedAssetToReceive?.code,
    setSelectedAssetToPay,
    setSelectedAssetToReceive,
  ]);

  useEffect(() => {
    if (
      isExchangeAssetsLoading ||
      assetsExchangeFrom.length === 0 ||
      assetsExchangeTo.length === 0
    ) {
      return;
    }

    const assetFromState = state?.asset;

    if (!assetFromState || assetCodeToReceive || assetCodeToPay) {
      return;
    }

    const assetFromExists = assetsExchangeFrom.some(
      asset => asset.code === assetFromState
    );

    if (assetFromExists) {
      setAssetCodeToPay(assetFromState);
    }

    const assetToExists = assetsExchangeTo.some(
      asset => asset.code === assetFromState
    );

    if (assetToExists) {
      setAssetCodeToReceive(assetFromState);
    }
  }, [
    state?.asset,
    isExchangeAssetsLoading,
    assetsExchangeFrom,
    assetsExchangeTo,
    assetCodeToPay,
    assetCodeToReceive,
  ]);

  const setAssetFromAmount = useCallback(
    (amount: string) => {
      setAmountToPay(amount);
      recalculateAmountToReceive(amount);
    },
    [setAmountToPay, recalculateAmountToReceive]
  );

  const onMaxClick = useCallback(() => {
    if (!selectedAssetToPay || !selectedAssetToPay?.available) return;

    setAssetFromAmount(String(selectedAssetToPay?.available));
  }, [selectedAssetToPay, setAssetFromAmount]);

  const setAssetToAmount = useCallback(
    (amount: string) => {
      setAmountToReceive(amount);
      recalculateAmountToPay(amount);
    },
    [setAmountToReceive, recalculateAmountToPay]
  );

  const getAssetFromDescription = useCallback(() => {
    const error =
      payFieldErrorBeforeClick?.message || formErrors.payFieldErrorAfterClick;

    if (error) {
      return error;
    }

    const { available, code } = selectedAssetToPay ?? {};

    if (typeof available === 'number' && code) {
      return (
        <AssetFromAvailableDescription
          code={code}
          available={available}
          onClick={() => setAssetFromAmount(String(available))}
        />
      );
    }

    return '';
  }, [
    payFieldErrorBeforeClick,
    formErrors.payFieldErrorAfterClick,
    selectedAssetToPay,
    setAssetFromAmount,
  ]);

  if (isExchangeAssetsLoading) {
    return <Loader />;
  }

  return (
    <Card className={card({ isMobile })}>
      <CardHeader className={header()}>
        <CardTitle className={title()}>{t('exchange.title')}</CardTitle>
      </CardHeader>
      <CardContent className={content()}>
        <Form {...form}>
          <form className={formStyles()}>
            <ExchangeInputSection
              type={EXCHANGE_SECTION_TYPES.FROM}
              title={t('exchange.youPay')}
              description={getAssetFromDescription()}
              isAmountError={
                payFieldErrorBeforeClick?.target === 'amount' ||
                !!formErrors.payFieldErrorAfterClick
              }
              isSelectError={
                payFieldErrorBeforeClick?.target === 'select' ||
                !!formErrors.payFieldErrorAfterClick
              }
              onAmountBlur={onPayAmountBlur}
              amount={amountToPay}
              resetFormErrors={resetFormErrors}
              setAmount={setAssetFromAmount}
              selectedAsset={selectedAssetToPay}
              assetsOptions={assetsExchangeFrom}
              onAssetChange={() => {
                if (!assetCodeToPay) return;

                setAmountToPay('');
                setAmountToReceive('');
              }}
              precision={selectedAssetToPay?.precision}
              onMaxClick={onMaxClick}
              onQuickSelect={setAssetCodeToPay}
            />

            <Separator childrenClassName="bg-card">
              <ExchangeRotateButton onClick={rotateAssets} />
            </Separator>

            <ExchangeInputSection
              type={EXCHANGE_SECTION_TYPES.TO}
              title={t('exchange.youReceive')}
              description={
                receiveFieldErrorBeforeClick?.message ||
                formErrors.receiveFieldErrorAfterClick ||
                exchangeRateDescription
              }
              isAmountError={!!formErrors.receiveFieldErrorAfterClick}
              isSelectError={
                !!receiveFieldErrorBeforeClick ||
                !!formErrors.receiveFieldErrorAfterClick
              }
              onAmountBlur={onReceiveAmountBlur}
              amount={amountToReceive}
              resetFormErrors={() => {
                resetFormErrors();
              }}
              setAmount={setAssetToAmount}
              selectedAsset={selectedAssetToReceive}
              assetsOptions={assetsExchangeTo}
              precision={selectedAssetToReceive?.precision}
              onQuickSelect={setAssetCodeToReceive}
            />

            <div className={controlsSection()}>
              {selectedAssetToPay && selectedAssetToReceive && (
                <ExchangePercentageSection
                  assetToPay={selectedAssetToPay}
                  setAmountToPay={amount => {
                    setAmountToPay(amount);
                    resetFormErrors();
                  }}
                  recalculateAmountToReceive={recalculateAmountToReceive}
                />
              )}

              {(assetCodeToReceive || assetCodeToPay) && (
                <Button
                  type="button"
                  size="lg"
                  disabled={
                    Number(amountToPay) > (selectedAssetToPay?.available ?? 0)
                  }
                  onClick={() => onSubmit(form.getValues())}>
                  {t('exchange.submitButton')}
                </Button>
              )}
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export { ExchangeScreen };
